<script setup lang="ts">
import { TagSelector } from '@/composables/charts/influx-charts/query-builders/InfluxTypes'
import { computed, ComputedRef, ref, Ref, watch } from 'vue'
import { useMeasurementStore } from '@/store/measurements'
import _ from 'lodash'

const props = withDefaults(
  defineProps<{
    measurement: string
    availableTagKeys?: string[] | null
    tagIndex: number
  }>(),
  {
    availableTagKeys: null,
    tagIndex: 0,
  }
)
const model = defineModel<TagSelector<string> | null>({ default: null })
const emit = defineEmits(['deleteTagSelector'])

const loadedTagKeys: Ref<string[]> = ref([])
const pickedTag: Ref<string | null> = ref(null)
const availableTagValues: Ref<string[]> = ref([])
const pickedTagValues: Ref<string[]> = ref([])
const useAllTagValues: Ref<boolean> = ref(true)
const groupByTagValues: Ref<boolean> = ref(true)
const allowEmptyTagValues: Ref<boolean> = ref(false)

const tagKeys: ComputedRef<string[]> = computed(() => {
  if (props.availableTagKeys !== null) return props.availableTagKeys
  return loadedTagKeys.value
})

async function loadAvailableTags() {
  if (props.availableTagKeys !== null) return
  loadedTagKeys.value = await useMeasurementStore().loadAvailableTagKeyForMeasurement(undefined, props.measurement)
  if (!pickedTag.value || !loadedTagKeys.value.includes(pickedTag.value)) pickedTag.value = loadedTagKeys.value[0] || null
}

async function loadAvailableTagValues() {
  if (!pickedTag.value) return
  availableTagValues.value = await useMeasurementStore().loadAvailableTagValuesForMeasurement(undefined, props.measurement, pickedTag.value)
}

watch(
  () => props.measurement,
  async () => {
    await loadAvailableTags()
  },
  { immediate: true }
)

watch(
  pickedTag,
  async () => {
    await loadAvailableTagValues()
  },
  { immediate: true }
)

watch(
  model,
  () => {
    if (!model.value) {
      pickedTag.value = null
      pickedTagValues.value = []
      useAllTagValues.value = true
      groupByTagValues.value = false
      allowEmptyTagValues.value = false
      return
    }
    if (model.value.tag !== pickedTag.value) pickedTag.value = model.value.tag
    if (model.value.useAll !== useAllTagValues.value) useAllTagValues.value = model.value.useAll
    if (model.value.groupByValues !== groupByTagValues.value) groupByTagValues.value = model.value.groupByValues
    if (model.value.allowEmptyValues !== allowEmptyTagValues.value) allowEmptyTagValues.value = model.value.allowEmptyValues
    if ((!model.value.useAll || !_.isEqual(model.value.specificValues, pickedTagValues.value)) && model.value.specificValues) {
      pickedTagValues.value = model.value.specificValues
    } else {
      pickedTagValues.value = []
    }
  },
  { immediate: true, deep: true }
)

watch(
  () => [pickedTag, pickedTagValues, useAllTagValues, groupByTagValues, allowEmptyTagValues],
  () => {
    if (!pickedTag.value) {
      model.value = null
      return
    }
    const newModel: TagSelector<string> = {
      tag: pickedTag.value,
      useAll: useAllTagValues.value,
      groupByValues: groupByTagValues.value,
      allowEmptyValues: allowEmptyTagValues.value,
    }
    if (!useAllTagValues.value && pickedTagValues.value.length) newModel.specificValues = pickedTagValues.value
    if (_.isEqual(model.value, newModel)) return
    model.value = newModel
  },
  { immediate: true, deep: true }
)
</script>

<template>
  <div
    class="mt-2 flex-fill d-flex flex-column ga-2"
    :class="{ 'mt-8': tagIndex > 0 }"
  >
    <div class="d-flex align-center justify-space-between ga-4">
      <v-avatar variant="tonal">
        <span class="text-body-1 font-weight-medium">
          {{ tagIndex + 1 }}
        </span>
      </v-avatar>
      <v-autocomplete
        v-model="pickedTag"
        prepend-icon="mdi-tag"
        min-width="200px"
        :items="tagKeys"
        variant="outlined"
        :label="$t('chart_sharing.creator.customize.tag_selectors.tag_key')"
        hide-details
      >
      </v-autocomplete>
      <v-btn
        class="rounded-circle"
        icon
        variant="tonal"
        color="error"
        @click="emit('deleteTagSelector', props.tagIndex)"
      >
        <v-icon
          icon="mdi-delete-forever"
          size="x-large"
        />
      </v-btn>
    </div>

    <div class="d-flex flex-row align-center justify-center gc-8 flex-wrap">
      <v-switch
        v-model="allowEmptyTagValues"
        :label="$t('chart_sharing.creator.customize.tag_selectors.generic.allow_empty')"
        hide-details
      />
      <v-switch
        v-model="groupByTagValues"
        :label="$t('chart_sharing.creator.customize.tag_selectors.generic.group_by')"
        hide-details
      />
      <v-switch
        v-model="useAllTagValues"
        :label="$t('chart_sharing.creator.customize.tag_selectors.generic.use_all')"
        hide-details
      />
    </div>

    <div
      v-if="!useAllTagValues"
      class="mb-4"
    >
      <v-autocomplete
        v-model="pickedTagValues"
        prepend-icon="mdi-tag-multiple"
        min-width="200px"
        :items="availableTagValues"
        :multiple="true"
        variant="outlined"
        :label="$t('chart_sharing.creator.customize.tag_selectors.generic.specific')"
        :disabled="useAllTagValues"
        chips
        closable-chips
        hide-details
      />
    </div>
  </div>
</template>
